export type ChangeType = 'added' | 'changed' | 'fixed' | 'removed' | 'security';

export interface ChangelogEntry {
  version: string;
  date: string;
  type: ChangeType;
  file: string;
  description: string;
}

// Changelog entries - newest first
// Version format: MAJOR.MINOR.PATCH
// MAJOR: Breaking changes or major features
// MINOR: New features, components
// PATCH: Bug fixes, small improvements
export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    version: '1.10.0',
    date: '2026-02-10',
    type: 'changed',
    file: 'CombinedSearchPro.tsx + advanced-search',
    description: 'Iterativ batch-sökning: "Sök alla flikar" kör nu upp till 3 omgångar där constraints propageras mellan varje omgång. Flikar med smala constraints (≤5 bokstäver) genererar SQL LIKE-villkor direkt istället för post-filtrering, vilket drastiskt minskar antalet resultat vid kopplade söktabbar.',
  },
  {
    version: '1.9.0',
    date: '2026-02-10',
    type: 'changed',
    file: 'AuthContext.tsx',
    description: 'Refaktorerat useAuth till en delad React Context Provider (AuthProvider). Eliminerar ~25 duplicerade profiles/user_roles-anrop vid sidladdning. Alla komponenter delar nu samma auth-tillstånd istället för att var och en gör egna nätverksanrop. Fixar även race condition som kunde förhindra att söktabbar sparades korrekt.',
  },
  {
    version: '1.8.6',
    date: '2026-02-09',
    type: 'changed',
    file: 'useAdvancedSearchTabs.ts',
    description: 'Sökresultat sparas inte längre i databasen – bara sökinställningar (ledtråd, bokstäver, längd, etc.) persisteras. Vid inladdning körs sökningarna om automatiskt via edge function. Eliminerar timeout-problemet helt och håller resultaten alltid aktuella.',
  },
  {
    version: '1.8.5',
    date: '2026-02-09',
    type: 'fixed',
    file: 'useAdvancedSearchTabs.ts',
    description: 'Fixat: Söktabbar sparades inte – DELETE timeout p.g.a. stora JSONB-resultat i 50+ rader skapade en spiral av dubbletter. Fix: (1) Kontrollera delete-resultat innan insert för att förhindra dubbletter, (2) Begränsa sparade resultat till max 200 per flik för att minska JSONB-storlek. Befintliga dubbletter rensade.',
  },
  {
    version: '1.8.4',
    date: '2026-02-08',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixat: (1) Begränsningsindikatorn visade stale data efter att en bokstav togs bort – getLinkedConstraints hämtar nu enbart constraints från ANDRA flikar, inte flikens egna possibleLetters. (2) Sökning krävde felaktigt en ifylld bokstav även med länkade celler – valideringen kontrollerar nu om celler är länkade till andra flikar istället för att kräva aktiva constraints.',
  },
  {
    version: '1.8.3',
    date: '2026-02-08',
    type: 'changed',
    file: 'AdvancedSearchOverview.tsx',
    description: 'Översikten i Avancerad sökning visar nu ett 2D-korsordsgrid baserat på länkade cellers positioner istället för en lista. BFS-algoritm placerar ord horisontellt/vertikalt med korsningar vid länkade celler. Numrerade ord med legend.',
  },
  {
    version: '1.8.2',
    date: '2026-02-07',
    type: 'added',
    file: 'AdvancedSearchOverview.tsx + CombinedSearchPro.tsx',
    description: 'Ny grafisk översikt i Avancerad sökning. LayoutGrid-ikon bredvid spara/rensa visar en kompakt karta över alla flikar med bokstavsmönster, resultatantal och färgkodade SVG-linjer för celllänkar.',
  },
  {
    version: '1.8.1',
    date: '2026-02-07',
    type: 'fixed',
    file: 'supabase/functions/advanced-search/index.ts',
    description: 'Fixat databas-timeout i sökfunktionen genom att byta ilike till like (data redan uppercase), möjliggör btree-indexanvändning inom 8s PostgREST-timeout',
  },
  {
    version: '1.8.0',
    date: '2026-02-06',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx + useAdvancedSearchTabs.ts + advanced-search',
    description: 'Åtgärdat 11 brister i Avancerad sökning: "Sök alla flikar" fungerar nu korrekt med sekventiell sökning per flik, delade typer i advancedSearchTypes.ts, ökad constraint-gräns (10→29 bokstäver), borttagen oanvänd kod (buildConstraintFilters, hitDatabaseLimit), fixade stale closures i autosave, ordlängdsfilter vid ledtrådsssökning, begränsad ledtrådshämtning till top 500, tydligare felmeddelanden vid länkkonflikter, och progressindikator vid batch-sökning.'
  },
  {
    version: '1.7.1',
    date: '2026-02-05',
    type: 'changed',
    file: 'CombinedSearchPro.tsx + advanced-search + useAutoSearch.ts',
    description: 'Sökning tillåts nu med bara ordlängd + länkade constraints (utan ifyllda bokstäver). Minimikrav: minst en bokstav, en ledtråd (2+ tecken), eller koppling till annan flik.'
  },
  {
    version: '1.7.0',
    date: '2026-02-05',
    type: 'added',
    file: 'advanced-search edge function + CombinedSearchPro.tsx',
    description: 'Server-side sökning i Avancerad sökning som söker genom HELA databasen utan 1000-radsbegränsning. Sökningen använder paginering för att hämta alla matchande ord. Tog bort varningen om databasbegränsning.'
  },
  {
    version: '1.6.0',
    date: '2026-02-05',
    type: 'added',
    file: 'CombinedSearchPro.tsx + useAutoSearch.ts + useKeyboardShortcuts.ts',
    description: 'Automatisk sökning vid bokstavsändring (600ms debounce). Nya kortkommandon: Ctrl+Enter sök, Ctrl+N ny flik, Ctrl+→/← byt flik. Ny knapp "Sök alla flikar" för batch-uppdatering. Förbättrad varning vid databasgräns med ikon och tydligare text.'
  },
  {
    version: '1.5.15',
    date: '2026-02-04',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Tabbar som nådde databasgränsen på 1000 resultat visar nu "(1000+)" istället för ett opålitligt filtrerat antal. Fetstil-markering inaktiveras för dessa tabbar.'
  },
  {
    version: '1.5.14',
    date: '2026-02-04',
    type: 'fixed',
    file: 'useAdvancedSearchTabs.ts',
    description: 'Fixade bugg där aktiv flik kunde ändras automatiskt vid sparning. Laddning av data sker nu endast en gång per session.'
  },
  {
    version: '1.5.13',
    date: '2026-02-04',
    type: 'added',
    file: 'CombinedSearchPro.tsx',
    description: 'Visar nu antalet filtrerade resultat på varje söktabb. Tabbar med minskat antal markeras med fetstil tills de öppnas.'
  },
  {
    version: '1.5.12',
    date: '2026-02-03',
    type: 'changed',
    file: 'ClueImportAdmin.tsx',
    description: 'Omdesignad bekräftelsedialog för import - tydligare sammanfattning med huvudord-status, kompaktare layout och kollapsbar detaljsektion.'
  },
  {
    version: '1.5.11',
    date: '2026-02-03',
    type: 'fixed',
    file: 'ClueImportAdmin.tsx',
    description: 'Fixar bugg där ord med dubbletter i databasen inte kunde hittas vid import (maybeSingle returnerar null vid flera träffar).'
  },
  {
    version: '1.5.10',
    date: '2026-02-03',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Visar nu varning när sökresultaten begränsas av databasgränsen (1000 rader). Användaren informeras om att fylla i fler bokstäver för att få kompletta resultat.'
  },
  {
    version: '1.5.9',
    date: '2026-02-03',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixade bugg där constraints inte propagerades korrekt genom komplexa länknätverk med 25+ flikar. Tomma bokstavsmängder (omöjliga positioner) propageras nu transituvt genom hela nätverket.'
  },
  {
    version: '1.5.8',
    date: '2026-02-03',
    type: 'changed',
    file: 'CombinedSearchPro.tsx',
    description: 'Visar nu exakt antal träffar istället för "500+" i Avancerad sökning. Antalet finns redan i minnet så detta kräver ingen extra beräkning.'
  },
  {
    version: '1.5.7',
    date: '2026-02-02',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixade bugg där bokstäver fylldes i automatiskt utan användarens kontroll. Auto-fill sker nu ENDAST för länkade celler där en annan flik har en ifylld bokstav som källa. Celler med befintligt innehåll berörs aldrig.'
  },
  {
    version: '1.5.6',
    date: '2026-02-02',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixade bugg där länkade celler kunde visa olika antal möjliga bokstäver. Constraint propagation uppdaterades: steg 1 filtrerar resultat, steg 2 synkroniserar länkgrupper efteråt. Detta säkerställer att alla celler i en länkgrupp alltid har identiska möjliga bokstäver.'
  },
  {
    version: '1.5.5',
    date: '2026-02-02',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixade bugg där bokstäver inte kunde raderas i länkade celler. Auto-fill effekten skapade en loop som omedelbart fyllde i bokstaven igen. Nu körs effekten endast när constraints ändras, inte vid varje tabs-uppdatering.'
  },
  {
    version: '1.5.4',
    date: '2026-02-02',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixade bugg där constraint-propagation inte fungerade för nya sessioner utan sökresultat. Nu inkluderas ifyllda bokstäver i formuläret som constraints, så att bokstäver propageras korrekt till länkade celler även när den andra tabben saknar sökresultat.'
  },
  {
    version: '1.5.3',
    date: '2026-02-02',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Fixade bugg där bokstäver inte kunde raderas i Avancerad sökning. Auto-fill logiken överskrev manuella ändringar genom att fylla i bokstäver igen direkt efter radering. Nu spåras auto-fyllda bokstäver för att undvika loop.'
  },
  {
    version: '1.5.2',
    date: '2026-02-01',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Implementerade constraint-aware sökning för länkade flikar. Databassökningen tar nu hänsyn till constraints från andra flikar INNAN resultaten hämtas, vilket löser problemet med tomma resultat vid parallella länkade sökningar.'
  },
  {
    version: '1.5.1',
    date: '2026-02-01',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Ökade sökgränser från 500 till 2000 resultat i Avancerad sökning för att visa fler träffar.'
  },
  {
    version: '1.5.0',
    date: '2026-02-01',
    type: 'added',
    file: 'useAdvancedSearchTabs.ts',
    description: 'Avancerade söktabbar sparas nu automatiskt för inloggade användare. Tabbar, sökresultat och celllänkar bevaras mellan sessioner.'
  },
  {
    version: '1.4.1',
    date: '2026-01-30',
    type: 'fixed',
    file: 'CombinedSearchPro.tsx',
    description: 'Förbättrad constraint propagation i Avancerad sökning - nu med transitiv propagering genom hela nätverket av länkade ord. Max 50 iterationer och global intersektionslogik för korrekta resultat med 20+ länkade ord.'
  },
  {
    version: '1.4.0',
    date: '2026-01-30',
    type: 'added',
    file: 'CryptoSolver.tsx',
    description: 'Ny flik "Kryptokryss" för att lösa kryptokorsord. Ange siffror för varje position, samma siffra = samma bokstav. Systemet beräknar möjliga ord och visar vilka bokstäver varje siffra motsvarar.'
  },
  {
    version: '1.3.1',
    date: '2026-01-27',
    type: 'added',
    file: 'CombinedSearchPro.tsx',
    description: 'Iterativ constraint-propagering i Avancerad sökning - möjliga bokstäver uppdateras nu i alla länkade flikar när filtrering minskar alternativen.'
  },
  {
    version: '1.3.0',
    date: '2026-01-26',
    type: 'changed',
    file: 'SearchTabs.tsx',
    description: 'Ersatte gamla Avancerad sökning med Pro-versionen och gjorde den tillgänglig för alla användare, inte bara admins.'
  },
  {
    version: '1.2.5',
    date: '2026-01-25',
    type: 'fixed',
    file: 'CellLinkDropdown.tsx',
    description: 'Förbättrad synlighet och klickbarhet för länkikonen - lättare att ta bort länkar även när cellen är tom.'
  },
  {
    version: '1.2.4',
    date: '2026-01-25',
    type: 'fixed',
    file: 'CellLinkDropdown.tsx',
    description: 'Begränsar cellval vid länkning baserat på vald ordlängd - kan inte längre välja positioner utanför flikens maxlängd.'
  },
  {
    version: '1.2.3',
    date: '2026-01-23',
    type: 'fixed',
    file: 'ClueImportAdmin.tsx',
    description: 'Lade till sessionsvalidering före analys och import för att förhindra RLS-fel vid utgången session.'
  },
  {
    version: '1.2.2',
    date: '2026-01-23',
    type: 'fixed',
    file: 'ClueImportAdmin.tsx',
    description: 'Återställde dubbelriktad synonymlänkning för enordssynonymer. Lade till bekräftelsedialog som visar exakt vad som kommer sparas innan import genomförs.'
  },
  {
    version: '1.2.1',
    date: '2026-01-22',
    type: 'changed',
    file: 'useAuth.ts',
    description: 'Lade till automatisk utloggning när session löper ut - användaren omdirigeras till login med tydligt meddelande istället för att se admin-gränssnitt med ogiltiga rättigheter.'
  },
  {
    version: '1.2.0',
    date: '2025-01-20',
    type: 'added',
    file: 'CombinedSearchPro.tsx + CrosswordCell.tsx',
    description: 'Länkade celler filtrerar nu resultat baserat på möjliga bokstäver från andra sökningar. Visuell indikator under länkade celler visar antal möjliga bokstäver.'
  },
  {
    version: '1.1.1',
    date: '2025-01-19',
    type: 'added',
    file: 'CombinedSearchPro.tsx + CellLinkDropdown.tsx',
    description: 'Ny funktion för att länka bokstavsceller mellan aktiva sökflikar. Dubbelriktad synkronisering och visuell färgmarkering.'
  },
  {
    version: '1.1.0',
    date: '2025-01-18',
    type: 'added',
    file: 'CombinedSearchPro.tsx + SearchTabs.tsx',
    description: 'Ny admin-exklusiv flik "Avancerad sökning Pro" för framtida Pro-funktioner.'
  },
  {
    version: '1.0.12',
    date: '2025-01-16',
    type: 'fixed',
    file: 'CombinedSearch.tsx',
    description: 'Förbättrad felhantering för timeout-fel vid sökning. Visar nu användarvänligt meddelande med tips om att ange fler bokstäver eller ordlängd.'
  },
  {
    version: '1.0.11',
    date: '2025-01-16',
    type: 'fixed',
    file: 'SearchTabs.tsx',
    description: 'Fixade scrollningsproblem i Avancerad sökning för Brave-webbläsaren.'
  },
  {
    version: '1.0.10',
    date: '2025-01-16',
    type: 'added',
    file: 'databas',
    description: 'Lade till GIN-index med pg_trgm på clues.clue_upper för snabbare ILIKE-sökningar (10-100x snabbare).'
  },
  {
    version: '1.0.9',
    date: '2025-01-15',
    type: 'added',
    file: 'AdminDeleteWordButton.tsx',
    description: 'Lade till raderingsknapp för ord i sökresultat (endast synlig för admin med bekräftelsedialog).'
  },
  {
    version: '1.0.8',
    date: '2025-01-15',
    type: 'fixed',
    file: 'database',
    description: 'Tog bort ~72 000 felaktiga omvända ledtrådskopplingar som skapades av importbuggen.'
  },
  {
    version: '1.0.7',
    date: '2025-01-15',
    type: 'fixed',
    file: 'ClueImportAdmin.tsx',
    description: 'Fixade bugg där manuell ledtrådsimport felaktigt skapade omvända kopplingar.'
  },
  {
    version: '1.0.6',
    date: '2025-01-15',
    type: 'changed',
    file: 'Statistics.tsx',
    description: 'Versionsnumret hämtas nu dynamiskt från changelog istället för statiskt värde.'
  },
  {
    version: '1.0.5',
    date: '2025-01-15',
    type: 'changed',
    file: 'DocsAdmin.tsx',
    description: 'Uppdaterade changelog-strukturen med versionsnummer och individuella ändringar.'
  },
  {
    version: '1.0.4',
    date: '2025-01-15',
    type: 'added',
    file: 'DocsAdmin.tsx',
    description: 'Skapade inline-vy för dokumentation och changelog i admin-gränssnittet.'
  },
  {
    version: '1.0.3',
    date: '2025-01-15',
    type: 'added',
    file: 'Admin.tsx',
    description: 'La till Docs-flik i admin-gränssnittet för att visa projektdokumentation.'
  },
  {
    version: '1.0.2',
    date: '2025-01-15',
    type: 'added',
    file: 'docs/CHANGELOG.md',
    description: 'Skapade changelog-fil för att spåra projektförändringar.'
  },
  {
    version: '1.0.1',
    date: '2025-01-15',
    type: 'added',
    file: 'docs/PROJECT_STRUCTURE.md',
    description: 'Skapade omfattande projektdokumentation med beskrivning av alla komponenter, hooks, databastabeller och arkitektur.'
  },
  {
    version: '1.0.0',
    date: '2025-01-15',
    type: 'fixed',
    file: 'textUtils.ts',
    description: 'Fixade removeAccents() så att svenska bokstäver (å, ä, ö) bevaras vid import av ord.'
  },
];

export const getCurrentVersion = (): string => {
  return CHANGELOG_ENTRIES[0]?.version || '1.0.0';
};
